import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Card } from '@safetyhub/ui';
import { authApi, verificationApi } from '../../lib/auth';

export default function PasswordResetVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // 토큰 검증 상태
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  // URL에서 토큰 추출 및 유효성 검증
  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');

    if (!tokenFromUrl) {
      setVerifying(false);
      setTokenValid(false);
      return;
    }

    setToken(tokenFromUrl);

    // 토큰 유효성 검증
    const validateToken = async () => {
      try {
        const result = await verificationApi.checkPasswordResetToken(tokenFromUrl);
        if (result.valid) {
          setTokenValid(true);
          if (result.email) {
            setMaskedEmail(result.email);
          }
        } else {
          setTokenValid(false);
        }
      } catch {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    validateToken();
  }, [searchParams]);

  // 비밀번호 유효성 검사
  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return '비밀번호는 8자 이상이어야 합니다.';
    }
    if (!/[a-zA-Z]/.test(password)) {
      return '비밀번호에 영문자가 포함되어야 합니다.';
    }
    if (!/\d/.test(password)) {
      return '비밀번호에 숫자가 포함되어야 합니다.';
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      return '비밀번호에 특수문자가 포함되어야 합니다.';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 유효성 검사
    if (!token.trim()) {
      setError('재설정 토큰이 필요합니다. 이메일의 링크를 다시 클릭해주세요.');
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    setLoading(true);

    try {
      await authApi.verifyPasswordReset(token, newPassword);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '비밀번호 재설정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 로딩 화면
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-ink-600">링크 확인 중...</p>
        </div>
      </div>
    );
  }

  // 토큰 무효 화면
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Link to="/" className="inline-block">
              <h1 className="text-3xl font-bold text-brand-600 hover:text-brand-700">SafetyHub</h1>
            </Link>
            <div className="mt-8">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
                <svg
                  className="h-8 w-8 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="mt-4 text-2xl font-bold text-ink-900">링크가 유효하지 않습니다</h2>
              <p className="mt-4 text-ink-600">
                비밀번호 재설정 링크가 만료되었거나 이미 사용되었습니다.
              </p>
              <p className="mt-2 text-sm text-ink-500">
                비밀번호 재설정을 다시 요청해주세요.
              </p>
            </div>

            <div className="mt-8 space-y-3">
              <Link
                to="/forgot-password"
                className="block w-full py-2 px-4 border border-transparent rounded-control shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700"
              >
                비밀번호 재설정 다시 요청
              </Link>
              <Link
                to="/login"
                className="block w-full py-2 px-4 border border-ink-300 rounded-control shadow-sm text-sm font-medium text-ink-700 bg-white hover:bg-ink-50"
              >
                로그인으로 돌아가기
              </Link>
            </div>
          </div>

          <p className="mt-8 text-center text-xs text-ink-500">
            SafetyHub &copy; 2025. All rights reserved.
          </p>
        </div>
      </div>
    );
  }

  // 성공 화면
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white rounded-card shadow p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-ink-900 mb-2">비밀번호 변경 완료</h2>
            <p className="text-ink-600 mb-6">
              새 비밀번호로 로그인해주세요.
            </p>
            <Button variant="primary" onClick={() => navigate('/login')} className="w-full">
              로그인하기
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* 헤더 */}
        <div className="text-center">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-brand-600 hover:text-brand-700">SafetyHub</h1>
          </Link>
          <h2 className="mt-4 text-2xl font-bold text-ink-900">비밀번호 재설정</h2>
          <p className="mt-2 text-sm text-ink-600">
            새 비밀번호를 입력해주세요.
          </p>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="rounded-card bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* 폼 */}
        <Card padding="xl" radius="lg" elevation="md">
          {maskedEmail && (
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-card">
              <p className="text-sm text-blue-800">
                <span className="font-medium">{maskedEmail}</span> 계정의 비밀번호를 재설정합니다.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 새 비밀번호 */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-ink-700">
                새 비밀번호
              </label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="8자 이상, 영문/숫자/특수문자 조합"
                className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
              />
            </div>

            {/* 새 비밀번호 확인 */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-ink-700">
                새 비밀번호 확인
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="새 비밀번호를 다시 입력하세요"
                className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
              />
            </div>

            <Button type="submit" variant="primary" disabled={loading} className="w-full">
              {loading ? '처리 중...' : '비밀번호 변경'}
            </Button>
          </form>
        </Card>

        {/* 로그인으로 돌아가기 */}
        <div className="text-center">
          <Link
            to="/login"
            className="text-sm text-ink-600 hover:text-ink-900"
          >
            로그인으로 돌아가기
          </Link>
        </div>
      </div>
    </div>
  );
}
